// ui/src/services/ticketApi.ts
// Wrapper Axios do módulo Service Desk / Ticket. Espelha os payloads do backend
// (/api/desk e /api/public/desk).
import api from '@/config/axios';

export type TicketStatus =
  | 'open'
  | 'triage'
  | 'in_progress'
  | 'waiting'
  | 'resolved'
  | 'closed'
  | 'cancelled'
  | 'escalated'
  | 'planned';
export type TicketType = 'incident' | 'request' | 'problem' | 'change';
export type Priority = 'low' | 'medium' | 'high' | 'critical';
export type TicketSource =
  | 'hub'
  | 'email'
  | 'form_public'
  | 'phone'
  | 'chat'
  | 'api'
  // Canais informados na abertura "em nome de" (migration 186):
  | 'teams'
  | 'whatsapp'
  | 'informal';
export type SlaState = 'ok' | 'attention' | 'warn' | 'breached' | 'met' | 'paused';
export type LinkType = 'related' | 'duplicate' | 'blocks' | 'blocked_by' | 'parent' | 'child';

export interface UserLite {
  id: number;
  nome?: string | null;
  display_name?: string | null;
  email?: string | null;
  department?: string | null;
  photo_url?: string | null;
  sAMAccountName?: string | null;
  /** Prestador externo (tipo_auth='external') — vem só da busca /users/search (pickers). */
  is_external?: boolean;
}

// ---- Central do Grupo (visão agregada no tenant master) ----
export interface GroupScopeTenant {
  id: number;
  nome: string;
  slug: string;
}
export interface GroupScopeCapability {
  enabled: boolean;
  master_tenant_id: number;
  tenants: GroupScopeTenant[];
}

// ---- Replicação da estrutura de entidades para tenants filhos (Tasks 6/7) ----
export interface EntityReplicationTenant {
  id: number;
  nome: string;
  slug: string;
}
export interface EntityReplicationCapability {
  can_replicate: boolean;
  tenants: EntityReplicationTenant[];
}

export interface SlaLayer {
  state: SlaState;
  remaining_seconds: number;
  due_at: string | null;
  goal_seconds: number | null;
  /**
   * O relógio está andando NESTE instante? Vem do servidor, que conhece o
   * expediente (fuso, dias e horário por tenant).
   *
   * ⚠ Opcional no tipo de propósito: o campo passou a ser enviado em
   * 06/09/2026 e uma resposta em cache pode não o ter. `andando()` de
   * `@/shared/chamados` trata ausente como "corre", que é o padrão seguro para
   * política 24/7.
   */
  relogio_correndo?: boolean;
  /** A política conta horas úteis (`true`) ou relógio de parede (`false`). */
  horario_comercial?: boolean;
}

export interface TicketSla {
  response: SlaLayer;
  resolution: SlaLayer;
  paused: boolean;
}

export interface TicketSummary {
  id: number;
  ticket_number: string | null;
  title: string;
  status: TicketStatus;
  priority: Priority;
  ticket_type: TicketType;
  created_at: string;
  requester?: UserLite | null;
  assignee?: UserLite | null;
  team?: { id: number; name: string; slug: string } | null;
  entity?: { id: number; name: string; slug: string; icon?: string | null; color?: string | null } | null;
  account?: { id: number; name: string; slug: string } | null;
  /**
   * Escola atendida (CRM | CS) — de quem é o chamado, não quem o abriu.
   * `name` é a RAZÃO SOCIAL; `nome_fantasia` é como as pessoas a chamam (vem
   * do espelho do CRM e pode faltar). Use `nomeDaEscola()` para escolher.
   */
  affectedAccount?: { id: number; name: string; slug: string; nome_fantasia?: string | null } | null;
  affected_account_id?: number | null;
  categoryUser?: { id: number; name: string } | null;
  affected_systems?: string[] | null;
  sla?: TicketSla;
  /** Filhos do grupo vinculado (acordeon na lista). */
  children?: Array<{ id: number; ticket_number: string | null; title: string; status: TicketStatus; priority: Priority }>;
  /** Central do Grupo: tenant de origem (presente só na visão agregada / detalhe cross). */
  tenant?: { id: number; nome: string } | null;
  /**
   * Balde do chamado, calculado no servidor (§4.6). Opcional porque o campo
   * nasceu com a fila nova e telas antigas não o consomem — mas quando existir
   * é ELE a verdade, nunca uma tradução do `status` feita no front.
   */
  bucket?: ChamadoBucket;
}

export interface TicketComment {
  id: number;
  ticket_id: number;
  user_id: number;
  body: string;
  is_internal: boolean;
  is_system: boolean;
  created_at: string;
  edited_at?: string | null;
  author?: UserLite | null;
  attachments?: TicketAttachment[];
  /** Quando preenchido, o comentário veio de um sub-chamado escalado (roll-up da cadeia). */
  chain_ticket_number?: string | null;
  /**
   * De onde a mensagem veio, quando não veio de uma pessoa escrevendo
   * (`ticket_comments.origin_kind`, migration 263): `escalation_return`,
   * `escalation_history`, `escalation_out`.
   *
   * ⚠ O backend SEMPRE o envia; faltava aqui. É ele que separa "mensagem que o
   * SISTEMA escreveu" de "nota interna de gente" — as duas são `is_internal`, e
   * chamar uma da outra foi defeito relatado no CRM | CS (chamado #000109,
   * 19/08/2026). Sem o campo declarado, o consumidor precisa de um tipo local
   * paralelo, que é a segunda fonte que diverge no primeiro `origin_kind` novo.
   */
  origin_kind?: string | null;
}

export interface TicketAttachment {
  id: number;
  ticket_id?: number | null;
  comment_id?: number | null;
  filename: string;
  mime_type: string;
  size_bytes: number;
  scan_status: 'pending' | 'clean' | 'infected' | 'error';
  created_at: string;
}

export interface TicketLink {
  id: number;
  ticket_id: number;
  related_id: number;
  link_type: LinkType;
  related?: { id: number; ticket_number: string | null; title: string; status: TicketStatus };
}

export interface TicketEvent {
  id: number;
  event_type: string;
  from_value?: string | null;
  to_value?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  actor?: UserLite | null;
}

export interface TicketDetail extends TicketSummary {
  description: string;
  impact: Priority;
  urgency: Priority;
  source: TicketSource;
  on_behalf_of_id?: number | null;
  assigned_user_id?: number | null;
  category_internal_id?: number | null;
  assignment_reason_id?: number | null;
  solution_summary?: string | null;
  csat_score?: number | null;
  resolved_at?: string | null;
  closed_at?: string | null;
  // Planejamento ("Em planejamento"): data de retomada (ISO) + justificativa + status de origem.
  planning_scheduled_at?: string | null;
  planning_justificativa?: string | null;
  pre_planning_status?: TicketStatus | null;
  categoryInternal?: { id: number; name: string } | null;
  assignmentReason?: { id: number; name: string } | null;
  slaPolicy?: { id: number; name: string } | null;
  onBehalfOf?: UserLite | null;
  comments?: TicketComment[];
  attachments?: TicketAttachment[];
  links?: TicketLink[];
  watchers?: Array<{ id: number; user?: UserLite }>;
  events?: TicketEvent[];
  can_act?: boolean;
  can_reopen?: boolean;
  can_finalize?: boolean;
  can_move_finalized?: boolean;
  can_link?: boolean;
  /** Campos do formulário preenchidos, prontos como tabela (rótulo + valor legível). */
  custom_fields_display?: Array<{ label: string; value: string; type?: string; items?: string[] }>;
  form_id?: number | null;
  parent_ticket_id?: number | null;
  assigned_entity_id?: number | null;
  entity?: { id: number; name: string; slug: string; icon?: string | null; color?: string | null; parent_id?: number | null; manager_user_id?: number | null; allow_escalation?: boolean; allow_link?: boolean } | null;
  is_child?: boolean;
  can_escalate?: boolean;
  /** Classificações obrigatórias p/ resolver (marca * no card Propriedades; só p/ quem atende). */
  required_params?: { category_internal: boolean; assignment_reason: boolean };
  // Escalonamento (cadeia separada do grupo)
  escalated_from_id?: number | null;
  escalatedFrom?: { id: number; ticket_number: string | null; title: string; status: TicketStatus } | null;
  escalations?: Array<{
    id: number; ticket_number: string | null; title: string; status: TicketStatus; priority: Priority;
    assigned_user_id?: number | null; assigned_entity_id?: number | null; created_at: string; resolved_at?: string | null;
    assignee?: UserLite | null; entity?: { id: number; name: string; slug: string; icon?: string | null; color?: string | null } | null;
  }>;
}

// ---- Entidades (árvore de atendimento) ----
export interface EntityKpi {
  abertos: number;
  em_atendimento: number;
  aguardando: number;
  resolvidos: number;
  fechados: number;
  sla_estourado: number;
  backlog: number;
}
export interface EntityNode {
  id: number;
  name: string;
  slug: string;
  parent_id?: number | null;
  icon?: string | null;
  color?: string | null;
  description?: string | null;
  is_open_for_requests: boolean;
  accept_external_form: boolean;
  support_email?: string | null;
  default_form_id?: number | null;
  allow_escalation?: boolean;
  allow_link?: boolean;
  is_active: boolean;
  sort_order: number;
  inherit_manager: boolean;
  inherit_agents: boolean;
  manager_user_id?: number | null;
  sla_policy_id?: number | null;
  sla_enabled?: boolean;
  /** Espelho de conteúdo do tenant pai (Task 6/7): != null → readonly no filho. */
  replica_origin_id?: number | null;
  /** Master-only: tenants filhos escolhidos para replicar esta entidade. */
  replicate_tenant_ids?: number[] | null;
  effective_managers?: UserLite[];
  agent_count?: number;
  children?: EntityNode[];
}
export interface EntityMember {
  user_id: number;
  role: 'agent' | 'manager';
  name?: string | null;
  email?: string | null;
  photo_url?: string | null;
}
export interface EntityDetail extends EntityNode {
  members: EntityMember[];
  manager_ids?: number[];
  effective_agent_ids: number[];
  kpis: EntityKpi;
}
export interface EntityMine {
  id: number; name: string; slug: string; icon?: string | null; color?: string | null; parent_id?: number | null;
  accept_external_form?: boolean;
}

export interface TicketKpis {
  abertos: number;
  aguardando: number;
  resolvidos: number;
  sla_estourado: number;
}

// ─────────────────────────────────────────────────────────────────────────
// Fila de chamados — GET /desk/tickets/fila
// Contrato da spec `docs/superpowers/specs/2026-09-05-fila-chamados-hub-design.md`
// (§4.5, verbatim). O payload é servido por `TicketFila.service.ts`.
//
// ⚠ `bucket` vem PRONTO do servidor em cada linha. O front NUNCA recalcula o
// balde a partir do status: a régua é `bucketDoStatus` (backend), sem default,
// e uma segunda cópia aqui divergiria no primeiro status novo — foi assim que
// "Fora do fluxo" apareceu como "Pendente" em duas telas dos Pedidos de Compra.
// ─────────────────────────────────────────────────────────────────────────
export type ChamadoBucket = 'aberto' | 'atendimento' | 'aguardando' | 'escalado' | 'fechado' | 'cancelado';

export interface FilaSla {
  estado: string | null;              // ok|attention|warn|breached|met|paused
  restante_segundos: number | null;   // negativo = estourado; null = sem política
  prazo: string | null;
  meta_segundos: number | null;
  pausado: boolean;
  em_risco: boolean;
}

export interface FilaPessoaRef { id: number; nome: string; foto: string | null }

export interface FilaChamado {
  id: number;
  numero: string;                     // já formatado (#000089)
  titulo: string;
  bucket: ChamadoBucket;              // vem PRONTO — o front nunca recalcula
  status: string;
  prioridade: string;
  origem: string;                     // ticket.source
  fila: { id: number; nome: string; icone: string | null; cor: string | null } | null; // cor = ticket_entities.color CRU; validar com deskUi.entityHexColor
  departamento: string | null;        // ⚠ requester.department — ver §4.7
  conta: string | null;               // account.name (0/386 hoje; chip condicional)
  escola: string | null;              // affectedAccount (0/386 no Hub; é do CRM | CS)
  categoria: string | null;           // ⚠ categoryInternal, NÃO categoryUser — §4.7
  solicitante: FilaPessoaRef | null;
  responsavel: FilaPessoaRef | null;
  interacoes: number;                 // comentários; 0 quando não houver
  ultima_resposta_em: string | null;  // último comentário PÚBLICO humano (ISO); null sem nenhum
  aberto_em: string | null;
  atualizado_em: string | null;
  tenant: { id: number; nome: string } | null;
  filhos: Array<{ id: number; numero: string; titulo: string; status: string; bucket: ChamadoBucket; prioridade: string }>;
  sla: FilaSla;
}

export interface FilaKpis {
  aberto: number; atendimento: number; aguardando: number;
  escalado: number; fechado: number;
  /**
   * ⚠ `cancelado` VEM no payload (e nas linhas), mas não é etapa de trabalho:
   * não é coluna do Quadro nem cartão da tira de estados. Está aqui para
   * ninguém somar os baldes achando que fecham com o total.
   */
  cancelado: number;
  em_risco: number;
  /**
   * Os QUATRO baldes de trabalho: aberto + atendimento + aguardando +
   * escalado. `fechado` e `cancelado` ficam fora (conferido contra o servidor
   * em 05/09: 2+7+2+1 = 12 = `total_aberto`, com `fechado:18` e
   * `cancelado:6` à parte).
   */
  total_aberto: number;
  filas: number;                      // entidades distintas no recorte
}

export interface FilaPessoa { user_id: number; nome: string; foto: string | null; abertos: number }

export interface FilaPayload {
  kpis: FilaKpis;
  chamados: FilaChamado[];
  filas: FilaPessoa[] | null;         // null = sem permissão (≠ [] = ninguém tem)
  somente_meus: boolean;
  truncado: boolean;                  // a leitura 1 bateu no teto de 1000
  janela: {
    fechados_desde: string | null;    // ISO da janela aplicada
    fechados_carregados: number;      // quantos vieram
    bucket_carregado: ChamadoBucket | null;
    /** Há outra página do balde encerrado pedido? (28/09/2026) */
    tem_mais?: boolean;
    /**
     * Cursor da próxima página do balde (`apos`); `null` sem próxima. Opaco:
     * a tela só o devolve (review do PR #412, 4ª rodada, nº 6).
     */
    proximo?: string | null;
    /** Página 2+: só as linhas do balde — KPIs e filas não valem. */
    somente_balde?: boolean;
  };
}

/** Recorte pessoal da fila (§4.4). `outro` exige `user_id`. */
export type FilaEscopo = 'minha' | 'todos' | 'nao_atribuidos' | 'abertos_por_mim' | 'outro';

export interface FilaParams {
  escopo?: FilaEscopo;
  /** Só com `escopo=outro`. */
  user_id?: number;
  /** Fila em foco; ausente = consolidado. */
  entity_id?: number;
  /** Carrega ESSE balde inteiro (o "carregar mais" da coluna janelada). */
  bucket?: ChamadoBucket;
  /** Página do balde encerrado (200 por vez). Legado — o store usa `apos`. */
  pagina?: number;
  /**
   * Cursor da página seguinte do balde encerrado (o `janela.proximo` da
   * anterior). Keyset, não OFFSET: um chamado tocado entre uma página e outra
   * não desloca as linhas que faltam.
   */
  apos?: string;
  /** Central do Grupo — repassados como no resto do módulo. */
  scope_group?: 1;
  tenant_id?: number;
}

export interface ListResult<T> {
  data: T[];
  pagination?: { page: number; limit: number; total: number };
}

export interface Team {
  id: number;
  name: string;
  slug: string;
  account_id?: number | null;
  manager_user_id?: number | null;
  wip_limit?: number | null;
  is_active: boolean;
}

export interface Account {
  id: number;
  name: string;
  slug: string;
  cost_center?: string | null;
  account_owner_id?: number | null;
  sponsor_user_id?: number | null;
  health_score?: number | null;
  health_status: 'excellent' | 'good' | 'fair' | 'poor' | 'at_risk';
  owner?: UserLite | null;
  sponsor?: UserLite | null;
}

export interface Category {
  id: number;
  name: string;
  slug: string;
  parent_id?: number | null;
  is_internal: boolean;
  is_active?: boolean;
  sort_order?: number;
  /** Entidades vinculadas (escopo). Vazio = vale para todas. */
  entity_ids?: number[];
}

export interface AssignmentReason {
  id: number;
  name: string;
  is_active: boolean;
  sort_order: number;
  /** Entidades vinculadas (escopo). Vazio = vale para todas. */
  entity_ids?: number[];
}

/** "Categoria" do formulário padrão (opções do campo field_key='categoria'). */
export interface DefaultFormCategory {
  exists: boolean;
  form_name?: string;
  options: string[];
  is_required: boolean;
}

/** Override de parâmetro por entidade: true/false = exceção; null = herda (pai → geral). */
export interface EntityDeskOverride {
  entity_id: number;
  require_category_internal: boolean | null;
  require_assignment_reason: boolean | null;
}

/** Parâmetros do desk: classificações obrigatórias para resolver o chamado. */
export interface DeskSettings {
  require_category_internal: boolean;
  require_assignment_reason: boolean;
  entity_overrides?: EntityDeskOverride[];
}

export interface SlaPolicy {
  id: number;
  name: string;
  priority_order: number;
  conditions?: { all?: any[]; any?: any[] } | null;
  response_seconds: number;
  resolution_seconds: number;
  escalation_seconds?: number | null;
  rca_seconds?: number | null;
  business_hours_only: boolean;
  pause_on_waiting_user: boolean;
  notify_manager?: boolean;
  alert_at_percentage: number;
  auto_escalate_on_breach: boolean;
  is_active: boolean;
}

export interface FormField {
  field_key: string;
  label: string;
  field_type: string;
  options?: unknown[] | null;
  help_text?: string | null;
  is_required: boolean;
  sort_order: number;
  maps_to?: string | null;
  /** Fonte de dados de select dinâmico ('centro_custo' | 'projeto' | 'empresa'); options ignorado. */
  data_source?: string | null;
  /** Largura no layout do formulário: 'full' (padrão), 'half' (1/2) ou 'third' (1/3). */
  width?: string | null;
  conditional_logic?: Record<string, unknown> | null;
}

/** Item de uma fonte de dados de select (centro de custo / projeto / empresa). */
export interface FormSourceItem {
  value: string;
  label: string;
}

export interface DeskForm {
  id?: number;
  slug: string;
  name: string;
  description?: string | null;
  is_public: boolean;
  is_active: boolean;
  /** Formulário padrão do catálogo (fallback global). */
  is_default?: boolean;
  target_team_id?: number | null;
  target_entity_id?: number | null;
  default_ticket_type: TicketType;
  default_category_user_id?: number | null;
  confirmation_message?: string | null;
  fields?: FormField[];
}

/** Catálogo de "Abrir chamado": um card por entidade-pai abrível. Todas usam o form padrão;
 *  o chamado cai na entidade clicada. */
export interface CatalogCard {
  id: number;
  name: string;
  slug: string;
  icon?: string | null;
  description?: string | null;
}
/** Formulário para render interno (solicitante autenticado). */
export interface RenderForm {
  id: number;
  slug: string;
  name: string;
  description?: string | null;
  confirmation_message?: string | null;
  default_ticket_type: TicketType;
  target_entity_id?: number | null;
  is_default?: boolean;
  fields: FormField[];
}

// ---- Formulário público por entidade (external form) ----
export interface EntityPublic {
  name: string;
  description: string | null;
  slug: string;
}
export interface DeskFormPublicField {
  field_key: string;
  label: string;
  field_type: string;
  options: string[] | null;
  help_text: string | null;
  is_required: boolean;
  sort_order: number;
}
export interface DeskFormPublic {
  slug: string;
  fields: DeskFormPublicField[];
}

export interface ListTicketsParams {
  status?: TicketStatus;
  priority?: Priority;
  team_id?: number;
  account_id?: number;
  assignee_id?: number;
  sla?: string;
  q?: string;
  scope?: 'all' | 'mine' | 'unassigned' | 'team';
  entity_id?: number;
  page?: number;
  limit?: number;
  paginate?: boolean;
  /** Central do Grupo: visão agregada (só tem efeito no master, com capability). */
  scope_group?: 1;
  tenant_id?: number;
}

export const ticketApi = {
  // chamados
  list(params: ListTicketsParams = {}): Promise<ListResult<TicketSummary>> {
    return api.get('/desk/tickets', { params }).then((r) => r.data);
  },
  listMine(params: ListTicketsParams = {}): Promise<ListResult<TicketSummary>> {
    return api.get('/desk/tickets/mine', { params }).then((r) => r.data);
  },
  kpis(entityId?: number, extra: Record<string, unknown> = {}): Promise<TicketKpis> {
    return api
      .get('/desk/tickets/kpis', { params: { ...(entityId ? { entity_id: entityId } : {}), ...extra } })
      .then((r) => r.data);
  },
  /**
   * Fila de chamados (§4.4 da spec 2026-09-05).
   *
   * Uma chamada devolve TUDO o que a tela precisa: os contadores (do servidor,
   * sobre o recorte inteiro), as linhas em aberto por completo, uma janela de
   * 30 dias dos encerrados e as filas do time. Com `bucket` informado, faz uma
   * leitura só daquele balde — é o "carregar mais" da coluna janelada.
   */
  fila(params: FilaParams = {}): Promise<FilaPayload> {
    return api.get('/desk/tickets/fila', { params }).then((r) => r.data);
  },
  /** Contagem de chamados novos (badge do menu) — escopada à cobertura do usuário. */
  newCount(extra: Record<string, unknown> = {}): Promise<{ count: number }> {
    return api.get('/desk/tickets/new-count', { params: extra }).then((r) => r.data);
  },
  /** Central do Grupo: capability da visão agregada no master. */
  groupScope(): Promise<GroupScopeCapability> {
    return api.get('/desk/group-scope').then((r) => r.data);
  },
  get(id: number): Promise<TicketDetail> {
    return api.get(`/desk/tickets/${id}`).then((r) => r.data);
  },
  create(payload: Record<string, unknown>): Promise<TicketDetail> {
    return api.post('/desk/tickets', payload).then((r) => r.data);
  },
  update(id: number, payload: Record<string, unknown>): Promise<TicketDetail> {
    return api.patch(`/desk/tickets/${id}`, payload).then((r) => r.data);
  },
  events(id: number): Promise<TicketEvent[]> {
    return api.get(`/desk/tickets/${id}/events`).then((r) => r.data);
  },
  // transições
  triage(id: number) {
    return api.post(`/desk/tickets/${id}/triage`).then((r) => r.data);
  },
  assign(id: number, payload: { assigned_user_id?: number | null; assigned_team_id?: number | null }) {
    return api.post(`/desk/tickets/${id}/assign`, payload).then((r) => r.data);
  },
  requestInfo(id: number, payload: { message?: string } = {}) {
    return api.post(`/desk/tickets/${id}/request-info`, payload).then((r) => r.data);
  },
  /** Coloca o chamado em planejamento (pausa o SLA); data de retomada é opcional, justificativa é obrigatória. */
  plan(id: number, payload: { scheduledAt?: string | Date; justificativa: string }) {
    return api.post(`/desk/tickets/${id}/plan`, payload).then((r) => r.data);
  },
  /** Reagenda a data/justificativa de um chamado já em planejamento. */
  editPlanning(id: number, payload: { scheduledAt?: string | Date | null; justificativa: string }) {
    return api.patch(`/desk/tickets/${id}/plan`, payload).then((r) => r.data);
  },
  /** Retoma manualmente ("Retomar agora") um chamado em planejamento. */
  resumePlanning(id: number) {
    return api.post(`/desk/tickets/${id}/plan/resume`).then((r) => r.data);
  },
  resolve(id: number, payload: { solution_summary: string; closure_reason?: string }) {
    return api.post(`/desk/tickets/${id}/resolve`, payload).then((r) => r.data);
  },
  close(id: number, payload: { csat_score?: number; csat_comment?: string } = {}) {
    return api.post(`/desk/tickets/${id}/close`, payload).then((r) => r.data);
  },
  reopen(id: number, payload: { reason?: string } = {}) {
    return api.post(`/desk/tickets/${id}/reopen`, payload).then((r) => r.data);
  },
  cancel(id: number, payload: { reason?: string } = {}) {
    return api.post(`/desk/tickets/${id}/cancel`, payload).then((r) => r.data);
  },
  reclassify(id: number, category_internal_id: number) {
    return api.post(`/desk/tickets/${id}/reclassify`, { category_internal_id }).then((r) => r.data);
  },
  setAssignmentReason(id: number, assignment_reason_id: number) {
    return api.post(`/desk/tickets/${id}/assignment-reason`, { assignment_reason_id }).then((r) => r.data);
  },
  /** Escala para outra entidade OU um usuário (sem entidade) — cria sub-chamado #_n. */
  escalate(id: number, payload: { target_entity_id?: number | null; target_user_id?: number | null; reason?: string; send_history?: boolean }) {
    return api.post(`/desk/tickets/${id}/escalate`, payload).then((r) => r.data);
  },
  /** Move o chamado para outra entidade (fila). */
  moveEntity(id: number, entity_id: number): Promise<TicketDetail | { moved: true }> {
    return api.post(`/desk/tickets/${id}/move-entity`, { entity_id }).then((r) => r.data);
  },
  // entidades (árvore de atendimento)
  entities(): Promise<EntityNode[]> {
    return api.get('/desk/entities').then((r) => r.data);
  },
  entitiesMine(): Promise<EntityMine[]> {
    return api.get('/desk/entities/mine').then((r) => r.data);
  },
  /** Catálogo de abertura (cards por entidade abrível + formulários agrupados). */
  openCatalog(): Promise<CatalogCard[]> {
    return api.get('/desk/entities/catalog').then((r) => r.data);
  },
  /** Todas as entidades ativas (flat, com depth) — para o picker de "mover" e vínculo de SLA. */
  entitiesAll(): Promise<Array<EntityMine & { depth: number; sla_policy_id?: number | null; default_form_id?: number | null; is_open_for_requests?: boolean }>> {
    return api.get('/desk/entities/all').then((r) => r.data);
  },
  /** Ids das entidades que o usuário gerencia (habilita "abrir em nome de"). */
  entitiesManaged(): Promise<number[]> {
    return api.get('/desk/entities/managed').then((r) => r.data);
  },
  /** Replicação da estrutura (Tasks 6/7): capability master-only + tenants filhos elegíveis. */
  entityReplicationCapability(): Promise<EntityReplicationCapability> {
    return api.get('/desk/entities/replication/capability').then((r) => r.data);
  },
  entity(id: number): Promise<EntityDetail> {
    return api.get(`/desk/entities/${id}`).then((r) => r.data);
  },
  entityKpis(id: number): Promise<EntityKpi> {
    return api.get(`/desk/entities/${id}/kpis`).then((r) => r.data);
  },
  createEntity(payload: Record<string, unknown>): Promise<EntityNode> {
    return api.post('/desk/entities', payload).then((r) => r.data);
  },
  updateEntity(id: number, payload: Record<string, unknown>): Promise<EntityNode> {
    return api.put(`/desk/entities/${id}`, payload).then((r) => r.data);
  },
  deleteEntity(id: number): Promise<void> {
    return api.delete(`/desk/entities/${id}`).then((r) => r.data);
  },
  addEntityMember(id: number, payload: { user_id: number; role?: 'agent' | 'manager' }): Promise<EntityDetail> {
    return api.post(`/desk/entities/${id}/members`, payload).then((r) => r.data);
  },
  removeEntityMember(id: number, userId: number): Promise<EntityDetail> {
    return api.delete(`/desk/entities/${id}/members/${userId}`).then((r) => r.data);
  },
  /** Busca de usuários do Desk (agentes) — corporativos, deduplicados. */
  searchUsers(search: string): Promise<UserLite[]> {
    return api.get('/desk/users/search', { params: { q: search } }).then((r) => r.data as UserLite[]);
  },
  /** Busca incluindo prestadores externos — "em nome de", cópia/CC, membro de entidade e atribuir. */
  searchUsersIncludingExternal(search: string): Promise<UserLite[]> {
    return api.get('/desk/users/search', { params: { q: search, include_external: true } }).then((r) => r.data as UserLite[]);
  },
  /** Usuários mencionáveis num chamado (participantes + agentes). */
  mentionable(id: number, q: string, internal = false): Promise<UserLite[]> {
    return api.get(`/desk/tickets/${id}/mentionable`, { params: { q, internal } }).then((r) => r.data as UserLite[]);
  },
  // comentários
  comments(id: number): Promise<TicketComment[]> {
    return api.get(`/desk/tickets/${id}/comments`).then((r) => r.data);
  },
  addComment(id: number, payload: { body: string; is_internal?: boolean; attachment_ids?: number[]; mentioned_user_ids?: number[] }) {
    return api.post(`/desk/tickets/${id}/comments`, payload).then((r) => r.data);
  },
  /** Só o autor, e só enquanto for o último comentário do chamado (backend valida). */
  deleteComment(commentId: number): Promise<void> {
    return api.delete(`/desk/comments/${commentId}`).then(() => undefined);
  },
  // vínculos / watchers
  links(id: number): Promise<TicketLink[]> {
    return api.get(`/desk/tickets/${id}/links`).then((r) => r.data);
  },
  addLink(id: number, payload: { related_id: number; link_type: LinkType }) {
    return api.post(`/desk/tickets/${id}/links`, payload).then((r) => r.data);
  },
  /** Agrupa chamados (grupo vinculado): o chamado atual vira pai; filhos espelham o status. */
  linkGroup(id: number, related_ids: number[]): Promise<TicketDetail> {
    return api.post(`/desk/tickets/${id}/link-group`, { related_ids }).then((r) => r.data);
  },
  /** Desvincula um chamado do grupo e restaura seu status. */
  unlink(id: number, related_id: number): Promise<TicketDetail> {
    return api.post(`/desk/tickets/${id}/unlink`, { related_id }).then((r) => r.data);
  },
  removeLink(linkId: number) {
    return api.delete(`/desk/links/${linkId}`).then((r) => r.data);
  },
  watchers(id: number) {
    return api.get(`/desk/tickets/${id}/watchers`).then((r) => r.data);
  },
  addWatcher(id: number, user_id: number) {
    return api.post(`/desk/tickets/${id}/watchers`, { user_id }).then((r) => r.data);
  },
  /**
   * Sobe imagem colada no editor de resposta ao R2 (endpoint do Desk, sem exigir editor do Wiki).
   * Ao responder um chamado existente, passe ticketId: a imagem é gravada no R2 do TENANT DO
   * CHAMADO (Central do Grupo) — sem isso ela chega quebrada para o solicitante do tenant filho.
   */
  uploadInlineImage(file: File, opts: { ticketId?: number } = {}): Promise<{ url: string }> {
    const fd = new FormData();
    fd.append('image', file);
    if (opts.ticketId) fd.append('ticket_id', String(opts.ticketId));
    return api.post('/desk/inline-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  // anexos
  uploadAttachment(file: File, opts: { ticketId?: number; commentId?: number; draftTicketId?: number } = {}): Promise<TicketAttachment> {
    const fd = new FormData();
    fd.append('file', file);
    if (opts.commentId) fd.append('comment_id', String(opts.commentId));
    // draftTicketId: rascunho do COMPOSER de resposta — continua rascunho (não vincula ao
    // chamado), mas nasce no tenant do chamado (Central do Grupo), senão o envio do
    // comentário não encontra o anexo e a resposta seria bloqueada com 422.
    if (!opts.ticketId && opts.draftTicketId) fd.append('ticket_id', String(opts.draftTicketId));
    const url = opts.ticketId ? `/desk/tickets/${opts.ticketId}/attachments` : '/desk/attachments/draft';
    return api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  /** Baixa o anexo (rota autenticada) e dispara o download no navegador. */
  async downloadAttachment(id: number, filename: string): Promise<void> {
    const resp = await api.get(`/desk/attachments/${id}/download`, { responseType: 'blob' });
    const url = URL.createObjectURL(resp.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  // apoio
  teams(): Promise<Team[]> {
    return api.get('/desk/teams').then((r) => r.data);
  },
  accounts(): Promise<Account[]> {
    return api.get('/desk/accounts').then((r) => r.data);
  },
  categories(internal = false, entityId?: number | null): Promise<Category[]> {
    return api.get('/desk/categories', { params: { internal, ...(entityId ? { entity_id: entityId } : {}) } }).then((r) => r.data);
  },
  assignmentReasons(entityId?: number | null): Promise<AssignmentReason[]> {
    return api.get('/desk/assignment-reasons', { params: { ...(entityId ? { entity_id: entityId } : {}) } }).then((r) => r.data);
  },
  assignmentReasonsAdmin(): Promise<AssignmentReason[]> {
    return api.get('/desk/admin/assignment-reasons').then((r) => r.data);
  },
  createAssignmentReason(payload: { name: string; sort_order?: number; is_active?: boolean; entity_ids?: number[] }): Promise<AssignmentReason> {
    return api.post('/desk/admin/assignment-reasons', payload).then((r) => r.data);
  },
  updateAssignmentReason(id: number, payload: Partial<{ name: string; sort_order: number; is_active: boolean; entity_ids: number[] }>): Promise<AssignmentReason> {
    return api.put(`/desk/admin/assignment-reasons/${id}`, payload).then((r) => r.data);
  },
  removeAssignmentReason(id: number) {
    return api.delete(`/desk/admin/assignment-reasons/${id}`).then((r) => r.data);
  },
  // "Categoria" do formulário padrão (opções do campo do solicitante)
  defaultFormCategory(): Promise<DefaultFormCategory> {
    return api.get('/desk/admin/default-form-category').then((r) => r.data);
  },
  updateDefaultFormCategory(payload: Partial<{ options: string[]; is_required: boolean }>): Promise<DefaultFormCategory> {
    return api.put('/desk/admin/default-form-category', payload).then((r) => r.data);
  },
  // Parâmetros: categorias administráveis (internal=true → "Cat. interna")
  categoriesAdmin(internal = true): Promise<Category[]> {
    return api.get('/desk/admin/categories', { params: { internal } }).then((r) => r.data);
  },
  createCategory(payload: { name: string; is_internal?: boolean; sort_order?: number; entity_ids?: number[] }): Promise<Category> {
    return api.post('/desk/admin/categories', payload).then((r) => r.data);
  },
  updateCategory(id: number, payload: Partial<{ name: string; sort_order: number; is_active: boolean; entity_ids: number[] }>): Promise<Category> {
    return api.put(`/desk/admin/categories/${id}`, payload).then((r) => r.data);
  },
  removeCategory(id: number) {
    return api.delete(`/desk/admin/categories/${id}`).then((r) => r.data);
  },
  deskSettings(): Promise<DeskSettings> {
    return api.get('/desk/admin/settings').then((r) => r.data);
  },
  updateDeskSettings(payload: Partial<DeskSettings>): Promise<Partial<DeskSettings>> {
    return api.put('/desk/admin/settings', payload).then((r) => r.data);
  },
  /** Exceção por entidade: true/false grava override; null volta a herdar (pai → geral). */
  updateEntityDeskSettings(entityId: number, payload: Partial<{ require_category_internal: boolean | null; require_assignment_reason: boolean | null }>): Promise<EntityDeskOverride> {
    return api.put(`/desk/admin/settings/entities/${entityId}`, payload).then((r) => r.data);
  },
  // SLA admin
  slaPolicies(): Promise<SlaPolicy[]> {
    return api.get('/desk/sla-policies').then((r) => r.data);
  },
  createSlaPolicy(payload: Record<string, unknown>) {
    return api.post('/desk/sla-policies', payload).then((r) => r.data);
  },
  updateSlaPolicy(id: number, payload: Record<string, unknown>) {
    return api.put(`/desk/sla-policies/${id}`, payload).then((r) => r.data);
  },
  removeSlaPolicy(id: number) {
    return api.delete(`/desk/sla-policies/${id}`).then((r) => r.data);
  },
  reorderSlaPolicies(ids: number[]) {
    return api.post('/desk/sla-policies/reorder', { ids }).then((r) => r.data);
  },
  /** Entidades vinculadas a uma política de SLA (ids). */
  slaPolicyEntities(id: number): Promise<number[]> {
    return api.get(`/desk/sla-policies/${id}/entities`).then((r) => r.data);
  },
  setSlaPolicyEntities(id: number, entity_ids: number[]) {
    return api.put(`/desk/sla-policies/${id}/entities`, { entity_ids }).then((r) => r.data);
  },
  // forms (admin)
  forms(): Promise<DeskForm[]> {
    return api.get('/desk/forms').then((r) => r.data);
  },
  getForm(id: number): Promise<DeskForm> {
    return api.get(`/desk/forms/${id}`).then((r) => r.data);
  },
  createForm(payload: Partial<DeskForm>) {
    return api.post('/desk/forms', payload).then((r) => r.data);
  },
  updateForm(id: number, payload: Partial<DeskForm>) {
    return api.put(`/desk/forms/${id}`, payload).then((r) => r.data);
  },
  setFormFields(id: number, fields: FormField[]) {
    return api.put(`/desk/forms/${id}/fields`, { fields }).then((r) => r.data);
  },
  removeForm(id: number) {
    return api.delete(`/desk/forms/${id}`).then((r) => r.data);
  },
  // abertura por formulário (catálogo) — free-for-auth
  /** Formulário PADRÃO de abertura (usado por todas as entidades do catálogo). */
  defaultForm(): Promise<RenderForm> {
    return api.get('/desk/forms/default').then((r) => r.data);
  },
  /** Formulário resolvido para a entidade (default_form_id da entidade → legado → global). */
  defaultFormForEntity(entityId: number): Promise<RenderForm> {
    return api.get(`/desk/forms/default/${entityId}`).then((r) => r.data);
  },
  /** Opções de uma fonte de dados de select ('centro_custo' | 'projeto' | 'empresa'). */
  formSource(source: string): Promise<FormSourceItem[]> {
    return api.get(`/desk/form-sources/${source}`).then((r) => r.data);
  },
  /** Render de um formulário ativo específico (sem exigir público). */
  renderForm(id: number): Promise<RenderForm> {
    return api.get(`/desk/forms/${id}/render`).then((r) => r.data);
  },
  /** Submete o formulário e abre o chamado na entidade escolhida (entity_id), com opção de
   *  abrir em nome de (on_behalf_of_id, só gestor) e "em cópia" (watcher_ids). */
  openForm(id: number, payload: { values: Record<string, unknown>; attachment_ids?: number[]; entity_id?: number | null; on_behalf_of_id?: number | null; source?: 'hub' | 'email' | 'teams' | 'whatsapp' | 'phone' | 'informal'; watcher_ids?: number[] }): Promise<TicketDetail> {
    return api.post(`/desk/forms/${id}/open`, payload).then((r) => r.data);
  },
  // (Customer Success removido em 2026-07-30 — módulo desativado)
  // público (sem auth)
  publicGetForm(slug: string): Promise<DeskForm> {
    return api.get(`/public/desk/forms/${slug}`).then((r) => r.data);
  },
  publicSubmit(slug: string, payload: { values: Record<string, unknown>; captchaToken?: string; requesterName?: string; requesterEmail: string }): Promise<{ ticket_number: string; tracking_token: string }> {
    return api.post(`/public/desk/forms/${slug}/submit`, payload).then((r) => r.data);
  },
  publicTrack(token: string): Promise<Record<string, any>> {
    return api.get(`/public/desk/track/${token}`).then((r) => r.data);
  },
  publicGetEntityForm(entitySlug: string): Promise<{ entity: EntityPublic; form: DeskFormPublic }> {
    return api.get(`/public/desk/entity/${encodeURIComponent(entitySlug)}`).then((r) => r.data);
  },
  publicSubmitByEntity(entitySlug: string, fd: FormData): Promise<{ ticket_number: string; tracking_token: string }> {
    return api.post(`/public/desk/entity/${encodeURIComponent(entitySlug)}/submit`, fd).then((r) => r.data);
  },
};

export default ticketApi;
